import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { fmtDateTime } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import {
  dsarFootprint, eraseDsar, fulfilDsar, listDsar, recordDsar, refuseDsar,
  type DsarRequest, type SubjectFootprint,
} from './api'

/**
 * Data-subject requests — access, rectification, erasure.
 *
 * ## The deadline is the feature
 *
 * The Data Protection Act 2019 gives a controller a limited window to answer. A system that
 * can export on demand but keeps no record of who asked, when, and what was done cannot
 * demonstrate compliance with any of it. So the queue is ordered by deadline and overdue
 * rows say so loudly.
 *
 * ## Erasure is a decision, not a delete
 *
 * The footprint marks what can be removed and what cannot, with the reason. Consent records,
 * a frozen nomination roll and the audit trail all stay — each is evidence belonging to a
 * process rather than data held about the subject, and a screen that implied otherwise would
 * set up a promise the erasure step then breaks.
 */
export function DsarPage() {
  const [rows, setRows] = useState<DsarRequest[] | null>(null)
  const [openOnly, setOpenOnly] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [footprint, setFootprint] = useState<{ id: string; data: SubjectFootprint } | null>(null)
  const mayManage = useCan('vault:dsar-manage')
  const mayErase = useCan('vault:dsar-erase')

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await listDsar(openOnly))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [openOnly])

  useEffect(() => { void load() }, [load])

  const act = async (row: DsarRequest, what: 'fulfil' | 'refuse' | 'erase' | 'footprint') => {
    setBusy(row.id)
    try {
      setError(null)
      if (what === 'footprint') {
        setFootprint({ id: row.id, data: await dsarFootprint(row.id) })
        return
      }
      if (what === 'fulfil') {
        const note = window.prompt('What was provided to the subject? (recorded)')
        if (note === null) return
        await fulfilDsar(row.id, note)
      } else if (what === 'refuse') {
        const reason = window.prompt(
          'Why is this refused? The subject is entitled to be told, and it is recorded:')
        if (!reason) return
        await refuseDsar(row.id, reason)
      } else if (what === 'erase') {
        if (!window.confirm(
          'This erases the subject\'s contact channels and outreach history, and redacts '
          + 'their registration row. Consent records, the nomination roll and the audit '
          + 'trail are KEPT — they are evidence, not data held about the subject. Continue?'))
          return
        await eraseDsar(row.id)
      }
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const overdue = rows?.filter((r) => r.overdue).length ?? 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-erp-lg font-semibold">Data-subject requests</h1>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            What people have asked about their own data, and the statutory clock on each.
          </p>
        </div>
        {mayManage && (
          <button type="button" onClick={() => setAdding(true)}
            className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            <Plus className="h-3.5 w-3.5" /> Record a request
          </button>
        )}
      </div>

      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <MetricCard label={openOnly ? 'Open' : 'All'} value={String(rows?.length ?? 0)} />
        <MetricCard label="Overdue" value={String(overdue)} />
      </div>

      {overdue > 0 && (
        <Note tone="bad">
          {overdue} request{overdue === 1 ? ' is' : 's are'} past the statutory deadline.
          A late answer is a compliance failure whether or not the answer is correct.
        </Note>
      )}

      <label className="inline-flex items-center gap-2 text-erp-sm">
        <input type="checkbox" checked={openOnly}
          onChange={(e) => setOpenOnly(e.target.checked)} />
        Open requests only
      </label>

      <ErrorStrip message={error} className="rounded-md border" />

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState title="No requests"
          hint="When somebody asks what is held about them, record it here — the row is the
                evidence that they asked and were answered." />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Reference</Th>
            <Th>Kind</Th>
            <Th>Subject</Th>
            <Th>Status</Th>
            <Th>Due</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={6} />
            : rows.map((r) => (
              <tr key={r.id}>
                <Td className="font-mono text-erp-xs">{r.reference}</Td>
                <Td className="text-erp-xs">{r.kind.toLowerCase()}</Td>
                <Td>
                  {r.subjectName ?? (
                    <span className="text-muted-foreground text-erp-xs">
                      nothing held for this person
                    </span>
                  )}
                </Td>
                <Td>
                  <span className={cn('text-erp-xs',
                    r.status === 'FULFILLED' && 'text-emerald-700 dark:text-emerald-400',
                    r.status === 'REFUSED' && 'text-amber-700 dark:text-amber-400')}>
                    {r.status.toLowerCase()}
                  </span>
                  {r.refusedReason && (
                    <span className="block text-erp-xs text-muted-foreground max-w-[20rem]">
                      {r.refusedReason}
                    </span>
                  )}
                </Td>
                <Td className="text-erp-xs">
                  {r.overdue ? (
                    <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400
                                     font-medium">
                      <AlertTriangle className="h-3 w-3" /> overdue
                    </span>
                  ) : r.completedAt ? (
                    <span className="text-muted-foreground">{fmtDateTime(r.completedAt)}</span>
                  ) : (
                    <span>{r.daysRemaining} day{r.daysRemaining === 1 ? '' : 's'} left</span>
                  )}
                </Td>
                <Td>
                  <div className="flex items-center justify-end gap-2">
                    {busy === r.id && <Loader2 className="h-3 w-3 animate-spin" />}
                    {mayManage && (
                      <button type="button" onClick={() => void act(r, 'footprint')}
                        className="text-erp-xs text-chocolate hover:underline">
                        What we hold
                      </button>
                    )}
                    {mayManage && !r.completedAt && (
                      <>
                        <button type="button" onClick={() => void act(r, 'fulfil')}
                          className="text-erp-xs text-chocolate hover:underline">Fulfil</button>
                        <button type="button" onClick={() => void act(r, 'refuse')}
                          className="text-erp-xs text-muted-foreground hover:underline">
                          Refuse
                        </button>
                      </>
                    )}
                    {mayErase && !r.completedAt && r.kind === 'ERASURE' && (
                      <button type="button" onClick={() => void act(r, 'erase')}
                        className="inline-flex items-center gap-1 text-erp-xs
                                   text-red-700 dark:text-red-400 hover:underline">
                        <Trash2 className="h-3 w-3" /> Erase
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      <RecordModal open={adding} onClose={() => setAdding(false)} onDone={load} />
      <FootprintModal footprint={footprint?.data ?? null}
        onClose={() => setFootprint(null)} />
    </div>
  )
}

function RecordModal({ open, onClose, onDone }: {
  open: boolean; onClose: () => void; onDone: () => void | Promise<void>
}) {
  const [nationalId, setNationalId] = useState('')
  const [kind, setKind] = useState('ACCESS')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setNationalId(''); setKind('ACCESS'); setNote(''); setError(null); setBusy(false)
  }, [open])

  const submit = async () => {
    setBusy(true)
    try {
      setError(null)
      await recordDsar({ nationalId: nationalId.trim(), kind, subjectNote: note || null })
      await onDone()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Record a data-subject request" width="md">
      <div className="space-y-3">
        <ErrorStrip message={error} className="rounded-md border" />
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">
            Subject's ID number
          </span>
          <input value={nationalId} onChange={(e) => setNationalId(e.target.value)}
            maxLength={64} className="erp-input h-9 w-full text-erp-sm" autoFocus />
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            className="erp-input h-9 w-full text-erp-sm">
            <option value="ACCESS">Access — what do you hold about me</option>
            <option value="RECTIFICATION">Rectification — correct it</option>
            <option value="ERASURE">Erasure — delete it</option>
            <option value="OBJECTION">Objection — stop processing it</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">
            What they said (optional)
          </span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            maxLength={4000} className="erp-input w-full text-erp-sm py-2" />
        </label>
        <Note>
          The ID number is indexed and discarded — it is never stored on the request. Recording
          this starts a 30-day statutory clock.
        </Note>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="erp-btn h-9 px-3 text-erp-sm">
            Cancel
          </button>
          <button type="button" disabled={busy || !nationalId.trim()}
            onClick={() => void submit()}
            className={cn('erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2',
              (busy || !nationalId.trim()) && 'opacity-50')}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Record
          </button>
        </div>
      </div>
    </Modal>
  )
}

function FootprintModal({ footprint, onClose }: {
  footprint: SubjectFootprint | null; onClose: () => void
}) {
  return (
    <Modal open={footprint !== null} onClose={onClose} width="lg"
      title="What is held about this person">
      {footprint && (
        <div className="space-y-3">
          {!footprint.foundOnRegister ? (
            <Note>
              Nothing is held for this subject. That is itself the answer, and it is worth
              giving them in writing.
            </Note>
          ) : (
            <>
              <ErpTable head={(
                <>
                  <Th>Where</Th>
                  <Th className="text-right">Rows</Th>
                  <Th>On erasure</Th>
                </>
              )}>
                {footprint.entries.map((e) => (
                  <tr key={e.table}>
                    <Td>
                      <span className="font-mono text-erp-xs">{e.table}</span>
                      <span className="block text-erp-xs text-muted-foreground">
                        {e.describes}
                      </span>
                    </Td>
                    <Td className="text-right tabular-nums">{e.rows}</Td>
                    <Td className="text-erp-xs">
                      {e.erasable ? (
                        <span className="text-muted-foreground">removed</span>
                      ) : (
                        <>
                          <span className="text-amber-700 dark:text-amber-400 font-medium">
                            kept
                          </span>
                          {e.retentionNote && (
                            <span className="block text-muted-foreground max-w-[24rem]">
                              {e.retentionNote}
                            </span>
                          )}
                        </>
                      )}
                    </Td>
                  </tr>
                ))}
              </ErpTable>
              <Note tone="info">
                Counts and table names only — the values are the export, which is a separate
                deliberate act. Browsing a footprint must not become a way to read somebody's
                file.
              </Note>
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
